import type { CreateClientPayload } from '@/api/clients';
import { autoValidityForStatus } from '@/lib/clientFormat';

export const WIZARD_STEP_LABELS = [
  'Информация о компании',
  'Персональные данные',
  'Баланс',
  'Тесты',
  'Цены',
  'Проверка данных',
];

/** Тест, отмеченный на шаге "Тесты" — данные из общего каталога (TestCatalogRow), без account id. */
export interface StagedTest {
  id: number;
  name: string;
  category: string | null;
  version: string | null;
  basePrice: number;
}

/**
 * Состояние всего мастера — один объект на все 6 шагов, живёт в
 * ClientCreatePage и передаётся шагам как пропсы. До клика "Создать" ничего
 * из этого не уходит на бэк (см. докблок ClientCreatePage.tsx).
 */
export interface WizardState {
  // Шаг 1 — компания + основной контакт
  name: string;
  mt1Login: string;
  isMt1HallAllowed: boolean;
  tutorIds: number[];
  type: string;
  tariffType: string;
  status: string;
  /** '' = бессрочно. */
  validity: string;
  notes: string;
  lastName: string;
  firstName: string;
  middleName: string;
  position: string;
  email: string;
  phone: string;

  // Шаг 2 — персональные данные
  operatorTemplateId: string;
  ogrn: string;
  address: string;

  // Шаг 3 — баланс (только для tariffType === 'ue'); всегда "выдать" — списывать
  // при создании нечего, баланс ещё нулевой (см. докблок StepBalance.tsx).
  // Пустой amount → buildCreatePayload() ниже просто не отправит balanceAction.
  balanceAction: { amount: string; comment: string };

  // Шаг 4 — выбранные библиотечные тесты (id → данные из каталога, для отображения на шагах 5-6)
  selectedTests: Map<number, StagedTest>;
  // Шаг 5 — цены: только реально отредактированные пользователем (иначе действует базовая цена по умолчанию)
  libraryPrices: Map<number, number>;
  monoscalePrices: Map<number, number>;
}

export function createInitialWizardState(): WizardState {
  return {
    name: '',
    mt1Login: '',
    isMt1HallAllowed: false,
    tutorIds: [],
    type: 'external',
    tariffType: 'ue',
    status: 'demo',
    validity: autoValidityForStatus('demo'),
    notes: '',
    lastName: '',
    firstName: '',
    middleName: '',
    position: '',
    email: '',
    phone: '',
    operatorTemplateId: '',
    ogrn: '',
    address: '',
    balanceAction: { amount: '', comment: '' },
    selectedTests: new Map(),
    libraryPrices: new Map(),
    monoscalePrices: new Map(),
  };
}

/** Читаемые названия обязательных полей — для баннера ошибки и подсветки конкретных полей (как в ClientDetailPage). */
export const REQUIRED_FIELD_LABELS: Record<string, string> = {
  name: 'Наименование компании',
  mt1Login: 'Наименование кабинета в Maintest 5i',
  email: 'Email',
  ogrn: 'ОГРН',
  address: 'Юридический адрес',
};

const MT1_LOGIN_PATTERN = /^[A-z@\-.\d]*$/i;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Отфильтровывает на лету всё, кроме латиницы/цифр/@/./- — тот же смысл, что
 * серверная регулярка mt1Login (валидация в CustomerController::store() в
 * Laravel: required|min:2|max:32|regex:/^[A-z@\-.\d]*$/i), но без её
 * побочного "хвоста" ([\]^_` — символы между Z и a в ASCII, попавшие в
 * диапазон A-z случайно, не осознанно). Кириллица, пробелы, "!" и подобное
 * так и не долетают до поля ввода, а не просто блокируют отправку постфактум.
 */
export function sanitizeMt1Login(value: string): string {
  return value.replace(/[^a-zA-Z0-9@.-]/g, '');
}

/** Шаг 1 — обязательны название компании, логин кабинета (формат/длина как в серверной валидации) и email. */
function validateStep1(state: WizardState): string[] {
  const missing: string[] = [];
  if (!state.name.trim()) missing.push('name');
  const login = state.mt1Login.trim();
  if (login.length < 2 || login.length > 32 || !MT1_LOGIN_PATTERN.test(login)) missing.push('mt1Login');
  if (!EMAIL_PATTERN.test(state.email.trim())) missing.push('email');
  return missing;
}

/**
 * Шаг 2 — ОГРН/адрес визуально обязательны только при статусе "Активен" (в
 * отличие от сервера, который требует их при любом статусе кроме "Демо" — см.
 * докблок ClientCreatePage.tsx: осознанное расхождение фронт-подсказки и
 * реальной серверной проверки для статуса "Отключён", редкого при создании).
 */
function validateStep2(state: WizardState): string[] {
  const missing: string[] = [];
  if (state.status === 'active') {
    if (!state.ogrn.trim()) missing.push('ogrn');
    if (!state.address.trim()) missing.push('address');
  }
  return missing;
}

/** Шаги 3-6 не блокируют переход — баланс/тесты/цены необязательны, шаг 6 только показывает итог. */
export function validateWizardStep(step: number, state: WizardState): string[] {
  if (step === 1) return validateStep1(state);
  if (step === 2) return validateStep2(state);
  return [];
}

export function buildCreatePayload(state: WizardState): CreateClientPayload {
  const amount = Number(state.balanceAction.amount);
  return {
    name: state.name.trim(),
    mt1Login: state.mt1Login.trim(),
    type: state.type,
    tariffType: state.tariffType,
    status: state.status,
    validity: state.validity || null,
    isMt1HallAllowed: state.isMt1HallAllowed,
    notes: state.notes.trim() || null,
    personalData: {
      operatorTemplateId: state.operatorTemplateId ? Number(state.operatorTemplateId) : null,
      ogrn: state.ogrn.trim() || null,
      address: state.address.trim() || null,
    },
    adminContact: {
      lastName: state.lastName.trim() || null,
      firstName: state.firstName.trim() || null,
      middleName: state.middleName.trim() || null,
      phone: state.phone.trim() || null,
      position: state.position.trim() || null,
      email: state.email.trim(),
    },
    tutorIds: state.tutorIds,
    balanceAction:
      state.tariffType === 'ue' && Number.isFinite(amount) && amount > 0
        ? {
            action: 'add',
            amount,
            comment: state.balanceAction.comment.trim() || undefined,
          }
        : null,
  };
}
